import { useState, Suspense } from 'react';

import {
  AppointmentGroupingResponse,
  queryKeys,
  type UpdateAppointmentGroupingRequest
} from 'generated';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

import { mutations, queries } from 'RestAPIs/rapini';

import { planUnitsAtom } from '../../atoms';

import { Typography } from '@mui/material';

import AddRouteDialog from './AddRouteDialog';
import { getPlanUnitIds } from '../../../../utils';
import { useQueryClient } from 'react-query';
import { StyledDialogFullWidth } from 'components/shared/Dialog/Dialog.styles';
import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';

export default function StructuralRoutesDialog({ open, handleClose }) {
  const { t } = useTranslation();
  const [showAddRouteDialog, setShowAddRouteDialog] = useState(false);
  const [appointmentGroupToEdit, setAppointmentGroupToEdit] = useState<
    AppointmentGroupingResponse | undefined
  >();
  const [planunits] = useRecoilState(planUnitsAtom);
  const organizationalUnitIds = getPlanUnitIds(planunits);
  const queryClient = useQueryClient();

  const { data: appointmentGroupings } = queries.useGetAppointmentGroupings(
    //@ts-expect-error TODO: BE should accept array of ids
    organizationalUnitIds.length > 0 ? organizationalUnitIds.join(',') : null,
    {
      enabled: open
    }
  );
  const { mutate: updateAppointmentGrouping } =
    mutations.useUpdateAppointmentGrouping();

  const columns = [
    {
      field: 'code',
      headerName: 'Route nummer',
      flex: 1,
      minWidth: 150
    },
    {
      field: 'description',
      headerName: 'Route naam',
      flex: 1,
      minWidth: 150
    },
    {
      field: 'start',
      headerName: t('startTime'),
      flex: 1,
      minWidth: 150
    },
    {
      field: 'end',
      headerName: t('endTime'),
      flex: 1,
      minWidth: 150
    },
    {
      field: 'organizationalUnitName',
      headerName: 'Planeenheid',
      flex: 1,
      minWidth: 150
    }
  ];

  const rows =
    appointmentGroupings?.map((a) => ({
      ...a,
      id: a.entityId,
      organizationalUnitName: a.organizationalUnit?.name
    })) ?? [];

  function onAddClick() {
    setAppointmentGroupToEdit(undefined);
    setShowAddRouteDialog(true);
  }

  function onEditClick(id) {
    const editValues = appointmentGroupings?.find((a) => a.entityId === id);

    setAppointmentGroupToEdit(editValues);
    setShowAddRouteDialog(true);
  }

  function onDisconnectClick(id) {
    const appointmentGrouping = appointmentGroupings?.find(
      (a) => a.entityId === id
    );
    if (appointmentGrouping) {
      const payload: UpdateAppointmentGroupingRequest = {
        entityId: appointmentGrouping?.entityId,
        code: appointmentGrouping.code,
        description: appointmentGrouping.description,
        start: appointmentGrouping.start,
        durationInSeconds: appointmentGrouping.durationInSeconds
      };

      updateAppointmentGrouping(payload, {
        onSuccess: () => {
          queryClient.invalidateQueries(queryKeys.getAppointmentGroupings.name);
        }
      });
    }
  }

  function handleAddRouteDialogClose() {
    setShowAddRouteDialog(false);

    setAppointmentGroupToEdit(undefined);
  }

  return (
    <StyledDialogFullWidth
      title={t('activityPlanning.structuralRoutes')}
      maxWidth="extralarge"
      open={open}
      handleClose={handleClose}
      onClose={handleClose}
    >
      <Suspense fallback={<Typography>{t('loading')}</Typography>}>
        <TableCRUDLayout
          columns={columns}
          rows={rows}
          editable={false}
          renderFormContainer={false}
          shouldRenderToolBar={false}
          hideCreateAction={false}
          onAddClick={onAddClick}
          onAddClickTitle={t('activityPlanning.addRoutes')}
          onEditClick={onEditClick}
          customActions={(id, row) => [
            ...(row?.organizationalUnitName
              ? [
                  {
                    tooltipTitle: t('activityPlanning.disconnect'),
                    icon: 'link-slash' as IconType,
                    onClick: onDisconnectClick
                  }
                ]
              : [])
          ]}
          addRowDialog={
            <AddRouteDialog
              appointmentGroup={appointmentGroupToEdit}
              open={showAddRouteDialog}
              handleClose={handleAddRouteDialogClose}
            />
          }
        />
      </Suspense>
    </StyledDialogFullWidth>
  );
}
